import React, { useEffect, useRef, useState } from 'react';
import './profileEdit.css';
import { useNavigate } from 'react-router-dom';
import { useApiServices } from '../../services/endpoints/ApiEndpoint';
import { setLocalStorage } from '../../utils/helper';
import { useAlert } from '../../components/alert-context/AlertContext';
import Button from '../../components/button/Button';

function getInitials(first?: string, last?: string) {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}

const ProfileEdit: React.FC = () => {
  const navigate = useNavigate();
  const { AuthInfoAsync, UpdateUserByIdAsync, UploadFileAsync, GetFileContentAsync } = useApiServices();
  const alert = useAlert();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [userId, setUserId] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarFileId, setAvatarFileId] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
  });

  // ── Load user info ────────────────────────────────────────────

  useEffect(() => {
    AuthInfoAsync().then(async res => {
      if (!res.isSuccess) return;
      const r = res.result;
      setUserId(r.id);
      setLocalStorage('userId', r.id);
      setForm({
        firstName: r.firstName ?? '',
        lastName: r.lastName ?? '',
        phone: r.phone ?? '',
        email: r.email ?? '',
        password: '',
      });
      if (r.image) {
        setAvatarFileId(r.image);
        try {
          const blob = await GetFileContentAsync(r.image);
          setAvatarUrl(URL.createObjectURL(blob));
        } catch { /* keep null */ }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Image selection ───────────────────────────────────────────

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setAvatarUrl(URL.createObjectURL(file));
  };

  // ── Submit ────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      alert.error({ message: 'กรุณากรอกชื่อและนามสกุล' });
      return;
    }

    setIsSaving(true);
    try {
      let imageId = avatarFileId ?? undefined;

      // Upload new avatar if selected
      if (pendingFile) {
        const fd = new FormData();
        fd.append('file', pendingFile);
        const uploadRes = await UploadFileAsync(fd);
        if (uploadRes.isSuccess) {
          imageId = uploadRes.result?.id ?? uploadRes.result;
        }
      }

      const payload: any = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone,
        email: form.email.trim(),
        password: form.password,
        ...(imageId ? { image: imageId } : {}),
      };

      const res = await UpdateUserByIdAsync(userId, payload);
      if (res.isSuccess) {
        setPendingFile(null);
        alert.success({
          message: 'บันทึกข้อมูลเรียบร้อยแล้ว',
          onConfirm: () => navigate('/profile'),
        });
      } else {
        alert.error({ message: res.message ?? 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง' });
      }
    } catch {
      alert.error({ message: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง' });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────

  return (
    <section className="pe-wrapper">
      <div className="pe-container lg:container lg:mx-auto">

        {/* Page header */}
        <div className="pe-page-header">
          <button className="pe-back-btn" onClick={() => navigate(-1)} aria-label="ย้อนกลับ">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="pe-title">ข้อมูลส่วนตัว</h1>
        </div>

        <form onSubmit={handleSubmit}>

          {/* Avatar */}
          <div className="pe-avatar-section">
            <div className="pe-avatar-wrap">
              {avatarUrl ? (
                <img className="pe-avatar-img" src={avatarUrl} alt="profile" />
              ) : (
                <div className="pe-avatar-placeholder">
                  {getInitials(form.firstName, form.lastName) || (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
                      <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                    </svg>
                  )}
                </div>
              )}
              <button
                type="button"
                className="pe-avatar-edit-btn"
                onClick={() => fileInputRef.current?.click()}
                aria-label="เปลี่ยนรูปโปรไฟล์"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              </button>
            </div>
            <button
              type="button"
              className="pe-change-photo-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              เปลี่ยนรูปโปรไฟล์
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
            />
          </div>

          {/* Form card */}
          <div className="pe-card">

            {/* Name row */}
            <div className="pe-row">
              <div className="pe-field">
                <label className="pe-label">ชื่อ <span className="pe-required">*</span></label>
                <input
                  type="text"
                  className="input input-bordered pe-input"
                  placeholder="กรอกชื่อ"
                  value={form.firstName}
                  onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                />
              </div>
              <div className="pe-field">
                <label className="pe-label">นามสกุล <span className="pe-required">*</span></label>
                <input
                  type="text"
                  className="input input-bordered pe-input"
                  placeholder="กรอกนามสกุล"
                  value={form.lastName}
                  onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                />
              </div>
            </div>

            {/* Phone */}
            <div className="pe-field">
              <label className="pe-label">เบอร์ติดต่อ</label>
              <div className="pe-input-wrap pe-input-disabled">
                <svg className="pe-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.63 3.38 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6 6l.83-.7a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <input
                  type="text"
                  className="input pe-input"
                  value={form.phone}
                  disabled
                />
              </div>
              <p className="pe-hint">หมายเลขโทรศัพท์ไม่สามารถเปลี่ยนแปลงได้</p>
            </div>

            {/* Email */}
            <div className="pe-field">
              <label className="pe-label">อีเมล <span className="pe-required">*</span></label>
              <div className="pe-input-wrap">
                <svg className="pe-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <input
                  type="email"
                  className="input input-bordered pe-input"
                  placeholder="กรอกอีเมล"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                />
              </div>
            </div>

            {/* Divider */}
            <div className="pe-divider">
              <span>เปลี่ยนรหัสผ่าน (ไม่บังคับ)</span>
            </div>

            {/* Password */}
            <div className="pe-field">
              <label className="pe-label">รหัสผ่านใหม่</label>
              <div className="pe-input-wrap">
                <svg className="pe-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="input input-bordered pe-input"
                  placeholder="กรอกรหัสผ่านใหม่"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="pe-eye-btn"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

          </div>

          {/* Save button */}
          <div className="pe-actions">
            <Button variant="primary" fullWidth className="max-w-[24rem]" disabled={isSaving}>
              {isSaving ? 'กำลังบันทึก...' : 'บันทึกการแก้ไข'}
            </Button>
          </div>

        </form>
      </div>
    </section>
  );
};

export default ProfileEdit;
